"use client";

import analytics, {
  pushToDataLayer,
  type IInitOptions,
  type IPurchaseOptions,
} from "./analytics";
import type { ListingLike } from "@/src/shared/types/listing";

// Lightweight hook-like helper (can be used in TSX/JSX files)
export default function useAnalytics() {
  return {
    init: (opts?: IInitOptions) => analytics.init(opts),
    push: (eventName: string, payload: Record<string, unknown> = {}) =>
      pushToDataLayer(eventName, payload),
    track: (eventName: string, payload: Record<string, unknown> = {}) =>
      analytics.track(eventName, payload),
    signin: (user?: { phone?: string | number; method?: string }) =>
      analytics.signin(user),
    signup: (user?: {
      phone?: string | number;
      method?: string;
      name?: string;
    }) => analytics.signup(user),
    // Accept either a single ListingLike or an array of ListingLike for convenience.
    view_item_list: (
      args: {
        items?: ListingLike | ListingLike[];
        list_name?: string;
        params?: Record<string, unknown>;
      } = {},
    ) => {
      const { items, list_name, params } = args;
      const itemsArray = Array.isArray(items) ? items : items ? [items] : [];
      return analytics.view_item_list({ items: itemsArray, list_name, params });
    },
    view_item: (args: {
      item: ListingLike;
      params?: Record<string, unknown>;
    }) => analytics.view_item(args),
    add_to_cart: (args: {
      item: ListingLike;
      value?: number | null;
      currency?: string;
      params?: Record<string, unknown>;
    }) => analytics.add_to_cart(args),
    begin_checkout: (
      args: {
        item?: ListingLike;
        value?: number | null;
        currency?: string;
        params?: Record<string, unknown>;
      } = {},
    ) => analytics.begin_checkout(args),
    purchase: (args: IPurchaseOptions) => analytics.purchase(args),
    customAction: (actionName: string, payload: Record<string, unknown> = {}) =>
      analytics.customAction(actionName, payload),
    page_view: (
      args: {
        page_title?: string;
        page_location?: string;
        params?: Record<string, unknown>;
      } = {},
    ) => analytics.page_view(args),
  };
}
